import mapboxgl from 'mapbox-gl';
import earcut from 'earcut';
import { BUILDING_FOOTPRINT_PREVIEW_STYLE } from '../helpers/building-footprint-template-preview.helpers';
import {
  getStoreyById,
  getStoreyElevation,
  getStoreyIndex,
} from '@shared/helpers/design/storey.helpers';
import {
  getSelectedStoreyRenderIndex,
  resolveStoreysForThreeJsMode,
} from './storey-render-targets.helpers';
import type { DraftShape, Storey } from '@shared/models/design/storey.model';
import type { Point2D } from '@shared/models/geometry.model';
import type { Coordinate } from '@shared/models/geography.model';
import { point2dsToCoordinates } from '@shared/helpers/design/shape.helpers';
import { convertMetricOffsetToCoordinate } from '@shared/helpers/geometry/coordinates.helpers';
import { hexToRgbArray } from '@shared/helpers/color.helpers';
import { canvasColors, mapRenderingConstants } from '../map.constants';
import { getShapeRingCoordinates } from '../helpers/map.helpers';
import type {
  CutoutVerticalSlice,
  PointData,
  ThreeJsMode,
  WireframePieceSlice,
  WireframeStoreySlice,
} from './types';
import { resolveThreeJsTargetStoreys } from './threejs-mode.helpers';
import { buildCrosshairVertices } from './crosshair-vertices.helpers';
import {
  buildRightAngleVertices,
  buildRightAngleVerticesForOpenPath,
} from './right-angle-vertices.helpers';
import { classifyCutoutKind } from '@shared/helpers/design/cutout.helpers';
import {
  getOpenShapePoints,
  getParentWallSegments,
  pointAtT,
  resolveMezzanineSegmentRuns,
} from './mezzanine-edge-runs.helpers';
import type { PolygonDragOrigin } from './translation-preview.helpers';

const TEMPLATE_FILL_RGB = hexToRgbArray(
  BUILDING_FOOTPRINT_PREVIEW_STYLE.mapFillColor,
);
const TEMPLATE_STROKE_RGB = hexToRgbArray(
  BUILDING_FOOTPRINT_PREVIEW_STYLE.mapStrokeColor,
);

type SelectedOutlineOptions = {
  blue: [number, number, number];
  halo: [number, number, number];
  scaleHalo: number;
  scaleMain: number;
};

type ElevationRing = {
  elevation: number;
  color: [number, number, number];
  selectedScales?: { halo: number; main: number };
  lineWidthScale?: number;
};

/**
 * Add outline and vertical edge segments for one storey.
 *
 * Vertices are emitted **per edge**: all line segments belonging to wall edge i
 * are contiguous in `vertices`, enabling per-element wireframe interleaving in
 * the render loop. Returns {@link WireframePieceSlice}s for non-mezzanine edges.
 * Mezzanines still emit one storey-level contiguous block (no wall pieces to
 * interleave with).
 * Non-mezzanine roof-elevation horizontals sit after the vertical segment; the
 * renderer draws them after the roof fill via {@link WireframePieceSlice.roofLineFirst}.
 * Cutout verticals are appended to `cutoutVerticalAccum` and drawn after all wall fills.
 */
function addStoreyOutlineAndVertical(
  vertices: PointData[],
  selectedOverlayVertices: PointData[],
  storeys: Storey[],
  storey: Storey,
  origin: Coordinate,
  originMercator: mapboxgl.MercatorCoordinate,
  isSelectedStorey: boolean,
  /** Outline / vertical color for non-selected rings on this storey. */
  guideRgb: [number, number, number],
  scaleVertical: number,
  outlineScale: number,
  selectedOptions: SelectedOutlineOptions | null,
  zoom: number | undefined,
  cutoutVerticalAccum: CutoutVerticalSlice[],
  opacity = 1,
): WireframePieceSlice[] {
  const base = getStoreyElevation(storeys, storey);
  const top = base + storey.height;
  const isMezzanine = Boolean(storey.mezzanine);
  const parentWallSegments = isMezzanine
    ? getParentWallSegments(storeys, storey)
    : [];

  const pieceSlices: WireframePieceSlice[] = [];

  for (let shapeIndex = 0; shapeIndex < storey.shapes.length; shapeIndex++) {
    const shape = storey.shapes[shapeIndex];
    if (!shape?.points?.length || shape.points.length < 3) continue;
    const isCoveredCutout =
      shape.cutout === true &&
      classifyCutoutKind(storeys, storey, shape) === 'atrium';
    const isCutoutShape = shape.cutout === true && !isMezzanine;
    const ring = getShapeRingCoordinates(shape, origin);
    if (ring.length < 2) continue;
    const openPoints = getOpenShapePoints(shape.points);

    // Build ordered elevation rings once per shape.
    const outlineElevations: ElevationRing[] = [];
    if (base < 0) outlineElevations.push({ elevation: 0, color: guideRgb });
    if (isSelectedStorey && selectedOptions) {
      outlineElevations.push({
        elevation: base,
        color: selectedOptions.blue,
        selectedScales: {
          halo: selectedOptions.scaleHalo,
          main: selectedOptions.scaleMain,
        },
      });
    } else {
      outlineElevations.push({ elevation: base, color: guideRgb });
    }
    if (!isMezzanine) {
      outlineElevations.push({ elevation: top, color: guideRgb });
    }

    // Deduplicate elevations (e.g. base == 0 case).
    const seenElevations = new Set<number>();
    const uniqueElevations = outlineElevations.filter((elev) => {
      if (seenElevations.has(elev.elevation)) return false;
      seenElevations.add(elev.elevation);
      return true;
    });

    const deferredCutoutVerticalParams: {
      a: [number, number];
      dashStart: number;
    }[] = [];

    // Emit per-edge: all segments for edge i are contiguous so the render loop
    // can draw fill#i → wireframe#i without rebinding large ranges.
    for (let edgeIndex = 0; edgeIndex < ring.length - 1; edgeIndex += 1) {
      const a = ring[edgeIndex];
      const b = ring[edgeIndex + 1];
      if (!a || !b) continue;

      const edgeFirst = vertices.length;
      let cumulativeDist = 0;

      const segmentRuns = isCoveredCutout
        ? [{ startT: 0, endT: 1, isDashed: true }]
        : resolveMezzanineSegmentRuns({
            isMezzanine,
            segmentIndex: edgeIndex,
            shapePoints: openPoints,
            parentWallSegments,
          });

      const pushHorizontalsAtElevation = (elev: ElevationRing) => {
        const { color, selectedScales, lineWidthScale: elevScale } = elev;
        const segmentScale = elevScale ?? outlineScale;
        const targetVertices = selectedScales
          ? selectedOverlayVertices
          : vertices;

        for (const run of segmentRuns) {
          const runStart = pointAtT(a, b, run.startT);
          const runEnd = pointAtT(a, b, run.endT);

          if (selectedScales) {
            pushLineSegmentMercator(
              targetVertices,
              runStart,
              elev.elevation,
              runEnd,
              elev.elevation,
              originMercator,
              selectedOptions!.halo,
              opacity,
              cumulativeDist,
              run.isDashed,
              selectedScales.halo,
            );
          }

          const segmentDist = pushLineSegmentMercator(
            targetVertices,
            runStart,
            elev.elevation,
            runEnd,
            elev.elevation,
            originMercator,
            color,
            opacity,
            cumulativeDist,
            run.isDashed,
            selectedScales?.main ?? segmentScale,
          );
          cumulativeDist += segmentDist;
        }
      };

      // Horizontals except roof elevation — drawn with wall fill (before roof plane).
      for (const elev of uniqueElevations) {
        if (!isMezzanine && elev.elevation === top) continue;
        pushHorizontalsAtElevation(elev);
      }

      // Vertical at corner a: solid shapes draw here; cutouts defer so wall quads sort on top first.
      if (!isMezzanine) {
        if (isCutoutShape) {
          const dashStart = isCoveredCutout ? cumulativeDist : 0;
          deferredCutoutVerticalParams.push({ a, dashStart });
          // Keep roof horizontal dash phase continuous for atrium (vertical not in buffer yet).
          if (isCoveredCutout) {
            cumulativeDist += mercatorVerticalSegmentDist(a, base, top);
          }
        } else {
          const verticalDist = pushLineSegmentMercator(
            vertices,
            a,
            base,
            a,
            top,
            originMercator,
            guideRgb,
            opacity,
            isCoveredCutout ? cumulativeDist : 0,
            isCoveredCutout,
            scaleVertical,
          );
          if (isCoveredCutout) cumulativeDist += verticalDist;
        }
      }

      // Roof-elevation horizontals appended after vertical so GL can draw them after roof fill.
      const roofLineFirst = vertices.length;
      if (!isMezzanine) {
        for (const elev of uniqueElevations) {
          if (elev.elevation !== top) continue;
          pushHorizontalsAtElevation(elev);
        }
      }
      const roofLineCount = vertices.length - roofLineFirst;

      const wallWireframeCount = roofLineFirst - edgeFirst;
      if (wallWireframeCount > 0 || roofLineCount > 0) {
        pieceSlices.push({
          storeyId: storey.id,
          shapeIndex,
          edgeIndex,
          first: edgeFirst,
          count: wallWireframeCount,
          ...(roofLineCount > 0 ? { roofLineFirst, roofLineCount } : {}),
        });
      }
    }

    if (isCutoutShape && deferredCutoutVerticalParams.length > 0) {
      const cvFirst = vertices.length;
      for (const { a: corner, dashStart } of deferredCutoutVerticalParams) {
        pushLineSegmentMercator(
          vertices,
          corner,
          base,
          corner,
          top,
          originMercator,
          guideRgb,
          opacity,
          dashStart,
          isCoveredCutout,
          scaleVertical,
        );
      }
      const cvCount = vertices.length - cvFirst;
      if (cvCount > 0) {
        cutoutVerticalAccum.push({
          storeyId: storey.id,
          shapeIndex,
          first: cvFirst,
          count: cvCount,
        });
      }
    }

    // Right-angle indicators on selected storey corners → always overlay.
    if (isSelectedStorey && zoom != null) {
      const rightAngleVerts = buildRightAngleVertices({
        shape,
        origin,
        elevation: base,
        originMercator,
        zoom,
      });
      selectedOverlayVertices.push(...rightAngleVerts);
    }
  }

  return pieceSlices;
}

/**
 * Build wireframe line vertices for storey outlines and vertical edges.
 */
export function buildWireframeVertices({
  storeys,
  otherBuildingStoreys = [],
  draftShape,
  templateDraftShapes,
  selectedStoreyId,
  origin,
  mode,
  originMercator,
  crosshairCoordinate,
  crosshairElevation,
  zoom,
  softenGuidesBelowSelected = false,
  penMode = false,
  otherBuildingsOpacity = 1,
  polygonDragOrigin,
  closeDraftShape = false,
}: {
  storeys: Storey[];
  /** Sibling buildings rendered as static 3D context (DEV-2576). */
  otherBuildingStoreys?: Storey[][];
  draftShape?: DraftShape;
  /** Complete preset rings; first is solid and following rings are cutouts. */
  templateDraftShapes?: DraftShape[];
  selectedStoreyId: Storey['id'] | undefined;
  origin: Coordinate;
  mode: ThreeJsMode;
  originMercator: mapboxgl.MercatorCoordinate;
  /** Optional coordinate for draft mode crosshair indicator */
  crosshairCoordinate?: Coordinate;
  /** Elevation in meters for the crosshair */
  crosshairElevation?: number;
  /** Current map zoom level for screen-space sizing of right-angle indicators */
  zoom?: number;
  /**
   * When true (2D view), storeys below the selection use a soft grey guide so
   * they stay distinguishable from black PDF ink. 3D keeps black guides.
   */
  softenGuidesBelowSelected?: boolean;
  /** Pen-tool draft: gray placed edges, blue rubber-band (DEV-2576). */
  penMode?: boolean;
  /** Multiplier for sibling wireframe alpha (click-anywhere fade). */
  otherBuildingsOpacity?: number;
  /** Original polygon ring shown beneath active edge, point, or shape drags. */
  polygonDragOrigin?: PolygonDragOrigin;
  /** Preset hover previews are complete polygons rather than open draft paths. */
  closeDraftShape?: boolean;
}): {
  vertices: Float32Array;
  vertexCount: number;
  storeySlices: WireframeStoreySlice[];
  pieceSlices: WireframePieceSlice[];
  cutoutVerticalSlices: CutoutVerticalSlice[];
  overlayVertices: Float32Array;
  overlayVertexCount: number;
} {
  const targets = resolveThreeJsTargetStoreys(storeys, selectedStoreyId, mode);

  const vertices: PointData[] = [];
  const overlayVertices: PointData[] = [];
  const storeySlices: WireframeStoreySlice[] = [];
  const pieceSlices: WireframePieceSlice[] = [];
  const cutoutVerticalSlices: CutoutVerticalSlice[] = [];

  const blueRgb = hexToRgbArray(canvasColors.lineBlue);
  const grayRgb = hexToRgbArray(canvasColors.lineGray);
  const selectedBlueRgb = hexToRgbArray(canvasColors.pointSelected);
  const blackRgb = hexToRgbArray('#000000');
  const guideBelowRgb = hexToRgbArray(canvasColors.lineGuideBelow);
  const whiteHaloRgb = hexToRgbArray(canvasColors.lineSelectedHalo);

  const scaleVertical = mapRenderingConstants.lineWidthScaleVertical;
  const scaleHorizontal = mapRenderingConstants.lineWidthScaleHorizontal;
  const scaleSelected =
    mapRenderingConstants.lineWidthSelectedPx /
    mapRenderingConstants.lineWidthPx;
  const scaleHalo =
    mapRenderingConstants.lineWidthSelectedHaloPx /
    mapRenderingConstants.lineWidthPx;

  // Draw first so the current selected outline covers the guide before the
  // pointer has moved away from the pickup position.
  if (polygonDragOrigin) {
    for (let index = 0; index < polygonDragOrigin.points.length; index += 1) {
      const startPoint = polygonDragOrigin.points[index];
      const endPoint =
        polygonDragOrigin.points[(index + 1) % polygonDragOrigin.points.length];
      if (!startPoint || !endPoint) continue;
      const start = convertMetricOffsetToCoordinate(origin, startPoint);
      const end = convertMetricOffsetToCoordinate(origin, endPoint);
      pushLineSegmentMercator(
        overlayVertices,
        [start.lng, start.lat],
        polygonDragOrigin.elevation,
        [end.lng, end.lat],
        polygonDragOrigin.elevation,
        originMercator,
        blackRgb,
        0.3,
        0,
        false,
        scaleHorizontal,
      );
    }
  }

  const selectedId =
    selectedStoreyId != null ? String(selectedStoreyId) : undefined;
  const selectedStorey = selectedStoreyId
    ? getStoreyById(storeys, selectedStoreyId)
    : undefined;
  const selectedIndex =
    selectedStorey !== undefined ? getStoreyIndex(storeys, selectedStorey) : -1;
  const selectedRenderIndex = getSelectedStoreyRenderIndex(
    storeys,
    selectedStoreyId,
  );

  for (const storey of targets) {
    const isSelected = String(storey.id) === selectedId;
    const storeyIndex = getStoreyIndex(storeys, storey);
    // Soft grey only in 2D — keeps below-floor guides off black PDF ink.
    const guideRgb =
      softenGuidesBelowSelected &&
      !isSelected &&
      selectedIndex >= 0 &&
      storeyIndex >= 0 &&
      storeyIndex < selectedIndex
        ? guideBelowRgb
        : blackRgb;
    const first = vertices.length;

    const edgeSlices = addStoreyOutlineAndVertical(
      vertices,
      overlayVertices,
      storeys,
      storey,
      origin,
      originMercator,
      isSelected,
      guideRgb,
      scaleVertical,
      scaleHorizontal,
      isSelected
        ? {
            blue: selectedBlueRgb,
            halo: whiteHaloRgb,
            scaleHalo,
            scaleMain: scaleSelected,
          }
        : null,
      isSelected ? zoom : undefined,
      cutoutVerticalSlices,
    );

    pieceSlices.push(...edgeSlices);

    const count = vertices.length - first;
    if (count > 0) {
      storeySlices.push({ storeyId: storey.id, first, count });
    }
  }

  // Static sibling buildings: own storey list so elevation/mezzanine stay local.
  // Single-storey mode matches the edited floor instead of drawing a full stack.
  for (const siblingStoreys of otherBuildingStoreys) {
    const siblingTargets = resolveStoreysForThreeJsMode(
      siblingStoreys,
      mode,
      selectedRenderIndex,
    );
    for (const storey of siblingTargets) {
      const first = vertices.length;
      const edgeSlices = addStoreyOutlineAndVertical(
        vertices,
        overlayVertices,
        siblingStoreys,
        storey,
        origin,
        originMercator,
        false,
        blackRgb,
        scaleVertical,
        scaleHorizontal,
        null,
        undefined,
        cutoutVerticalSlices,
        otherBuildingsOpacity,
      );
      pieceSlices.push(...edgeSlices);
      const count = vertices.length - first;
      if (count > 0) {
        storeySlices.push({ storeyId: storey.id, first, count });
      }
    }
  }

  // Draft shape, crosshair → always-on-top overlay.
  const visibleDraftShapes =
    templateDraftShapes ?? (draftShape ? [draftShape] : []);
  if (templateDraftShapes?.length) {
    const storey = getStoreyById(storeys, selectedStoreyId);
    const base = storey ? getStoreyElevation(storeys, storey) : 0;
    const rings = templateDraftShapes.map((shape) =>
      point2dsToCoordinates(origin, shape).map(({ lng, lat }) =>
        mapboxgl.MercatorCoordinate.fromLngLat({ lng, lat }, base),
      ),
    );
    const points = rings.flat();
    const holes: number[] = [];
    let offset = 0;
    rings.forEach((ring, index) => {
      if (index > 0) holes.push(offset);
      offset += ring.length;
    });
    const indices = earcut(
      points.flatMap(({ x, y }) => [x, y]),
      holes,
    );
    for (const index of indices) {
      const point = points[index];
      if (!point) continue;
      overlayVertices.push({
        x: point.x - originMercator.x,
        y: point.y - originMercator.y,
        z: point.z - originMercator.z,
        ox: 0,
        oy: 0,
        oz: 0,
        side: 0,
        along: 0,
        r: TEMPLATE_FILL_RGB[0],
        g: TEMPLATE_FILL_RGB[1],
        b: TEMPLATE_FILL_RGB[2],
        opacity: BUILDING_FOOTPRINT_PREVIEW_STYLE.mapOpacity,
        distance: 0,
        isDashed: false,
        lineWidthScale: 0,
      });
    }
  }
  for (const visibleDraftShape of visibleDraftShapes) {
    if (!visibleDraftShape.points || visibleDraftShape.points.length < 2) {
      continue;
    }
    const storey = getStoreyById(storeys, selectedStoreyId);
    const base = storey ? getStoreyElevation(storeys, storey) : 0;

    const path: [number, number][] = point2dsToCoordinates(
      origin,
      visibleDraftShape,
    ).map(({ lng, lat }) => {
      const next: [number, number] = [lng, lat];
      return next;
    });

    let cumulativeDist = 0;
    const draftColor = penMode ? grayRgb : blueRgb;

    const segmentCount =
      templateDraftShapes || closeDraftShape ? path.length : path.length - 1;
    for (let i = 0; i < segmentCount; i += 1) {
      const a = path[i];
      const b = path[(i + 1) % path.length];
      if (!a || !b) continue;

      const segmentDist = pushLineSegmentMercator(
        overlayVertices,
        a,
        base,
        b,
        base,
        originMercator,
        templateDraftShapes ? TEMPLATE_STROKE_RGB : draftColor,
        templateDraftShapes ? BUILDING_FOOTPRINT_PREVIEW_STYLE.mapOpacity : 1,
        cumulativeDist,
        !penMode && !templateDraftShapes,
        templateDraftShapes ? scaleHorizontal : 1,
      );
      cumulativeDist += segmentDist;
    }

    if (zoom != null && !penMode && !templateDraftShapes) {
      const previewPoint = crosshairCoordinate
        ? ([crosshairCoordinate.lng, crosshairCoordinate.lat] as [
            number,
            number,
          ])
        : undefined;
      const rightAngleVerts = buildRightAngleVerticesForOpenPath({
        path,
        elevation: base,
        originMercator,
        zoom,
        previewPoint,
      });
      overlayVertices.push(...rightAngleVerts);
    }
  }

  if (crosshairCoordinate) {
    const storey = getStoreyById(storeys, selectedStoreyId);
    const elevation =
      crosshairElevation ?? (storey ? getStoreyElevation(storeys, storey) : 0);

    let lastPointCoordinate: { lng: number; lat: number } | undefined;
    if (draftShape?.points && draftShape.points.length >= 1) {
      const allCoords = point2dsToCoordinates(origin, draftShape);
      const lastCoord = allCoords[allCoords.length - 1];
      if (lastCoord) {
        lastPointCoordinate = lastCoord;
      }
    }

    const crosshairVerts = buildCrosshairVertices({
      coordinate: crosshairCoordinate,
      elevation,
      originMercator,
      lastPointCoordinate,
      penMode,
    });
    overlayVertices.push(...crosshairVerts);
  }

  const packed = packWireframeVertices(vertices);
  const packedOverlay = packWireframeVertices(overlayVertices);

  return {
    vertices: packed.vertices,
    vertexCount: packed.vertexCount,
    storeySlices,
    pieceSlices,
    cutoutVerticalSlices,
    overlayVertices: packedOverlay.vertices,
    overlayVertexCount: packedOverlay.vertexCount,
  };
}

/**
 * Pack PointData array into a Float32Array with high/low precision split.
 * Exported for use by crosshair and other vertex builders.
 */
export function packWireframeVertices(vertices: PointData[]): {
  vertices: Float32Array;
  vertexCount: number;
} {
  const vertexCount = vertices.length;
  if (vertexCount <= 0) return { vertices: new Float32Array(), vertexCount: 0 };

  const out = new Float32Array(vertexCount * 21);

  for (let v = 0; v < vertexCount; v += 1) {
    const data = vertices[v];
    if (!data) continue;

    const {
      x,
      y,
      z,
      ox,
      oy,
      oz,
      side,
      along,
      r,
      g,
      b,
      opacity,
      distance,
      isDashed,
      lineWidthScale,
    } = data;

    const hx = Math.fround(x);
    const hy = Math.fround(y);
    const hz = Math.fround(z);
    const lx = x - hx;
    const ly = y - hy;
    const lz = z - hz;

    const hox = Math.fround(ox);
    const hoy = Math.fround(oy);
    const hoz = Math.fround(oz);
    const lox = ox - hox;
    const loy = oy - hoy;
    const loz = oz - hoz;

    const o = v * 21;
    out[o] = hx;
    out[o + 1] = hy;
    out[o + 2] = hz;
    out[o + 3] = lx;
    out[o + 4] = ly;
    out[o + 5] = lz;
    out[o + 6] = hox;
    out[o + 7] = hoy;
    out[o + 8] = hoz;
    out[o + 9] = lox;
    out[o + 10] = loy;
    out[o + 11] = loz;
    out[o + 12] = side;
    out[o + 13] = along;
    out[o + 14] = r;
    out[o + 15] = g;
    out[o + 16] = b;
    out[o + 17] = opacity;
    out[o + 18] = distance;
    out[o + 19] = isDashed ? 1 : 0;
    out[o + 20] = lineWidthScale;
  }

  return { vertices: out, vertexCount };
}

/**
 * Mercator-space length of a vertical segment at one lng/lat from base to top altitude.
 * Matches {@link pushLineSegmentMercator} so dashed cutout outlines stay phase-aligned when verticals are deferred.
 */
function mercatorVerticalSegmentDist(
  lngLat: Point2D,
  baseMeters: number,
  topMeters: number,
): number {
  const start = mapboxgl.MercatorCoordinate.fromLngLat(
    { lng: lngLat[0], lat: lngLat[1] },
    baseMeters,
  );
  const end = mapboxgl.MercatorCoordinate.fromLngLat(
    { lng: lngLat[0], lat: lngLat[1] },
    topMeters,
  );
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const dz = end.z - start.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Push a line segment in mercator space with an explicit width scale.
 */
function pushLineSegmentMercator(
  vertices: PointData[],
  startLngLat: Point2D,
  startAltitudeMeters: number,
  endLngLat: Point2D,
  endAltitudeMeters: number,
  origin: mapboxgl.MercatorCoordinate,
  color: [number, number, number],
  opacity: number,
  startDist: number,
  isDashed: boolean,
  lineWidthScale: number,
): number {
  const start = mapboxgl.MercatorCoordinate.fromLngLat(
    { lng: startLngLat[0], lat: startLngLat[1] },
    startAltitudeMeters,
  );
  const end = mapboxgl.MercatorCoordinate.fromLngLat(
    { lng: endLngLat[0], lat: endLngLat[1] },
    endAltitudeMeters,
  );

  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const dz = end.z - start.z;
  const segmentDist = Math.sqrt(dx * dx + dy * dy + dz * dz);

  const [r, g, b] = color;

  const p1 = {
    x: start.x - origin.x,
    y: start.y - origin.y,
    z: start.z - origin.z,
  };
  const p2 = {
    x: end.x - origin.x,
    y: end.y - origin.y,
    z: end.z - origin.z,
  };

  // Add 6 vertices for 2 triangles per segment.
  const addVertex = (
    curr: typeof p1,
    other: typeof p1,
    side: number,
    along: number,
    dist: number,
  ) => {
    vertices.push({
      x: curr.x,
      y: curr.y,
      z: curr.z,
      ox: other.x,
      oy: other.y,
      oz: other.z,
      side,
      along,
      r,
      g,
      b,
      opacity,
      distance: dist,
      isDashed,
      lineWidthScale,
    });
  };

  // Tri 1
  addVertex(p1, p2, 1, -1, startDist);
  addVertex(p1, p2, -1, -1, startDist);
  addVertex(p2, p1, -1, 1, startDist + segmentDist);

  // Tri 2
  addVertex(p2, p1, -1, 1, startDist + segmentDist);
  addVertex(p2, p1, 1, 1, startDist + segmentDist);
  addVertex(p1, p2, 1, -1, startDist);

  return segmentDist;
}
